import React, { useEffect, useState } from "react"
import NextApp, { AppProps, AppContext } from "next/app"
import { Provider } from "reakit"
import Head from "next/head"
import styled, { ThemeProvider } from "styled-components"
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles"
import { Normalize } from "styled-normalize"
import ZutoAnalyticsHelper from "@zuto-internal/zuto-analytics-helper"
import BaseTheme from "@zuto/theme"
import { FontStyle } from "@zuto/utilities"
import { Header } from "@zuto/organisms"
import Box from "@zuto/core/src/box"
import $console from "../../packages/utilities/src/$console"
import Global from "../style/Global"
import getSource from "../config/Sources"
import Source from "../types/Source"
import { fireReactGAEvent } from "../components/Questions/utils"
import returnChannelName from "../utils/utils"
import PreInterstitialV1 from "../components/Interstitial/PreInterstitialV1"
import { FeatureSwitches } from "../components/ApplicationForm/ApplicationFormContext"
import "../components/ApplicationForm/ApplicationForm.css"
import FirstTimeApplicantPreInterstitialV1 from "../components/Interstitial/FirstTimeApplicantPreInterstitialV1"
import InterstitialWrapper from "../components/Interstitial"
import FormlessInterstitialWrapper, {
  InterstitialProps
} from "../components/FormlessInterstitial"
import { createAppTheme } from "@zuto/theme/src/mui/utils/createAppTheme"
import { tonalPalettes, tonalPalettesMSM } from "@zuto/theme/src/mui/Colors"
import dynamic from "next/dynamic"
import emotionCreateCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { handleCSPViolation } from "../utils/csp/reportCSP"
import { getValueFromHeader } from "../utils/csp/getValueFromHeader"

const MicrosoftClarity = dynamic(
  () => import("../components/shared/MicrosoftClarity"),
  { ssr: false }
)


const StyledSkipLink = styled(Box)`
  width: 120px;
  position: absolute;
  left: calc(50% - 60px);
  transform: translateY(-110%);
  background: white;
  text-decoration: none;
  transition: transform 0.3s;
  &:focus {
    transform: translateY(0%);
    outline: none;
    border: 1px solid #1a56f1;
  }
`

interface CustomAppProps extends AppProps {
  nonce: string
}

const App = ({
  Component,
  pageProps,
  nonce
}: CustomAppProps): JSX.Element => {
  // this is needed at entry point to pass the nonce to webpack - fixed issue with styled-components not being able to load styles
  __webpack_nonce__ = nonce
  // needed for csp to pass nonce to @emotion package which is where styled-components gets its 'styled' object
  const correctlyNoncedEmotionCache = emotionCreateCache({ key: 'nonce', nonce: nonce })

  const [theme, setTheme] = useState<BaseTheme | undefined>(undefined)
  const [logo, setLogo] = useState<React.ReactElement | undefined>(undefined)
  const [logoLabel, setLogoLabel] = useState<string | undefined>(undefined)
  const [additionalLogo, setAdditionalLogo] =
    useState<React.ReactElement | undefined>(undefined)
  const [showRightIcon, setShowRightIcon] = useState(false)
  const [width, setWidth] = useState<number>()
  const [showForm, setShowForm] = useState(false)
  const {
    customerId,
    lenderDetails,
    experimentName,
    featureSwitches,
    attribution,
    prefillData
  } = pageProps

  const isMoneybarnAndClearscorePreapproved =
    customerId === "clearscore" &&
    lenderDetails?.lenderName === "moneybarn" &&
    lenderDetails?.preapproved
  const isTotallyMoneyPreapprovedAutolend =
    customerId === "totallymoney" &&
    lenderDetails?.lenderName === "autolend" &&
    lenderDetails?.preapproved
  const {
    secureDataIconAtTop,
    reviewPageV3,
    reviewPageV3PreApproval,
    isTrustpilotV1,
    msmThemeV1,
    formlessJourneyV1
  } = featureSwitches ?? {}

  let sourceConfig: Source | undefined
  const { source } = attribution ?? {}
  if (source) {
    sourceConfig = getSource(source)
  }
  const sourceLogoName = sourceConfig ? sourceConfig.logoName : undefined

  const isInFormlessCtaJourney = (source === 'carmoola' || source === 'experian') && !!formlessJourneyV1

  const isFreedomFinanceAroPreApprovalControlMarsh =
    sourceLogoName === "ClearscoreLogo" &&
    lenderDetails?.preapproved &&
    lenderDetails?.lenderName &&
    lenderDetails?.lenderName.toLowerCase() === "marsh" &&
    !reviewPageV3PreApproval

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth)
  }

  useEffect(() => {
    setWidth(window.innerWidth)
    window.addEventListener("resize", handleWindowSizeChange)

    return () => window.removeEventListener("resize", handleWindowSizeChange)
  }, [])

  const isMobile = Boolean(width && width <= 768)

  const getTheme = async () => {
    const brandedTheme = (await import("@zuto/theme/src/zuto")).default
    setTheme(brandedTheme)
  }

  const initGTM = () => {
    fireReactGAEvent({
      category: "Application Form",
      action: "experiment_assigned",
      label: experimentName,
      channel: returnChannelName(attribution) ?? "default"
    })
    if (prefillData) {
      fireReactGAEvent({
        category: "Application Form",
        action: "SetLenderName",
        label: lenderDetails?.lenderName ?? "Zuto"
      })
    }
  }

  const initZA = async () => {
    const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
    let financeType = null

    if (prefillData && source === "freedomfinance") {
      financeType = "prefill"
    }

    if (prefillData && lenderDetails) {
      financeType = lenderDetails?.preapproved ? "preapproved" : "prefill"
    }

    try {
      zutoAnalyticsHelper.sendCustomEvent({
        eventType: "experiment-set",
        eventCategory: "new experiment set",
        eventAction: experimentName,
        conversationalAppForm: false,
        lenderName: lenderDetails
          ? lenderDetails?.lenderName.toLowerCase()
          : "zuto",
        financeType: financeType,
        featureSwitches
      })
    } catch (e) {
      $console.error("zutoAnalyticsHelper.sendCustomEvent error", e)
    }
  }

  const clickedTrustpilot = () => {
    fireReactGAEvent({
      category: "Application Form",
      action: "Trustpilot Clicked",
      label: "Header"
    })
  }

  const getLogo = async () => {
    if (sourceLogoName) {
      try {
        // If adding new logos to the below package, you may need to delete the .next folder and then re-run yarn dev for the static files to be regenerated locally.
        let loadedLogo = (
          await import(`@zuto/icons/src/${sourceLogoName}.inline.svg`)
        ).default
        if (sourceLogoName === "AutoTrader") {
          const reducedHeaderLogos = isMobile
            ? (
              await import(
                `../../packages/icons/src/logos/small/mobile/${sourceLogoName}.inline.svg`
              )
            ).default
            : (
              await import(
                `../../packages/icons/src/logos/small/desktop/${sourceLogoName}.inline.svg`
              )
            ).default
          loadedLogo = reducedHeaderLogos
        }
        setLogo(loadedLogo)
        setLogoLabel(`${sourceConfig?.displayName ?? "partner"} logo`)
        setShowRightIcon(true)
      } catch (err) {
        setShowRightIcon(false)
        $console.error(`Could not load logo for ${sourceLogoName}`)
      }
    } else {
      setShowRightIcon(true)
    }
  }

  // it's not on autolend which is the other freedomfinance aro preapproval control journey
  const getAdditionalLogo = async () => {
    if (isFreedomFinanceAroPreApprovalControlMarsh) {
      const additionalLoadedLogo = (
        await import("../../packages/icons/src/Marsh.inline.svg")
      ).default
      setAdditionalLogo(additionalLoadedLogo)
    }
  }

  useEffect(() => {
    initZA()
    initGTM()
    getTheme()
    getLogo()
    getAdditionalLogo()
    window.addEventListener('securitypolicyviolation', handleCSPViolation)
    return () => window.removeEventListener('securitypolicyviolation', handleCSPViolation)
  }, [])

  useEffect(() => {
    getLogo()
  }, [isMobile])

  useEffect(() => { __webpack_nonce__ = nonce }, [nonce])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowForm(true)
    }, 2000)
    return () => clearTimeout(timeout)
  }, [showForm])

  const evaluatePageToRender = (featureSwitches: FeatureSwitches) => {
    switch (true) {
      case featureSwitches.interstitialV1:
        return renderPageWithAutotradersInterstitial()
      case featureSwitches.preInterstitialV1:
        return renderPreInterstitialV1()
      case featureSwitches.firstTimeApplicantPreInterstitialV1:
        return renderfirstTimeApplicantPreInterstitialV1()
      case featureSwitches.requiredDetailsV1:
        return source === "clearscore"
          ? renderFromlessInterstitial({
            source,
            experimentName,
            isPreapproved: lenderDetails?.preapproved
          })
          : renderPage()
      case featureSwitches.msmThemeV1 && featureSwitches.shouldRenderMSMInterstitial:
        return renderPageWithAutotradersInterstitial()
      default:
        return renderPage()
    }
  }

  const renderPage = () => (
    <>
      <Header
        trustpilotOnClick={clickedTrustpilot}
        logo={logo}
        logoLabel={logoLabel}
        additionalLogo={additionalLogo}
        showRightIcon={showRightIcon}
        isTwoPagesJourneyExperiment={
          isMoneybarnAndClearscorePreapproved ||
          isTotallyMoneyPreapprovedAutolend
        }
        secureDataIconAtTop={secureDataIconAtTop}
        isMobile={isMobile}
        isReviewPage={reviewPageV3}
        isTrustpilotV1={isTrustpilotV1}
        source={source}
        isInMSMExperiment={msmThemeV1}
        isInFormlessCtaJourney={isInFormlessCtaJourney}
      />
      <main id="mainContent">
        <Component {...pageProps} />
      </main>
    </>
  )

  const renderFromlessInterstitial = ({
    source,
    experimentName,
    isPreapproved
  }: InterstitialProps): JSX.Element => (
    <>
      <FormlessInterstitialWrapper
        source={source}
        experimentName={experimentName}
        isPreapproved={isPreapproved}
        requiredDetailsV1={featureSwitches.requiredDetailsV1}
      />
      {showForm && renderPage()}
    </>
  )

  const renderPageWithAutotradersInterstitial = (): JSX.Element => (
    <>
      <InterstitialWrapper source={source} experimentName={experimentName} />
      {showForm && renderPage()}
    </>
  )

  const renderPreInterstitialV1 = (): JSX.Element => (
    <>
      <PreInterstitialV1 experimentName={experimentName} />
      {showForm && renderPage()}
    </>
  )

  const renderfirstTimeApplicantPreInterstitialV1 = (): JSX.Element => (
    <>
      <FirstTimeApplicantPreInterstitialV1 experimentName={experimentName} />
      {showForm && renderPage()}
    </>
  )

  const selectedPaletteTheme = createAppTheme(
    msmThemeV1 ? tonalPalettesMSM : tonalPalettes
  )

  return (
    <>
      <CacheProvider value={correctlyNoncedEmotionCache}>
        <Head>
          <title>Apply for Car Finance | Get a finance quote with no impact on your credit score</title>
          <meta name="title" content="Apply for Car Finance | Get a finance quote with no impact on your credit score" />
          <meta
            name="description"
            content="Apply for car finance with Zuto, check your eligibility with no impact on your credit score and get a decision in 60 seconds."
          />
        </Head>
        <MicrosoftClarity nonce={nonce} />
        {theme && (
          <MuiThemeProvider theme={selectedPaletteTheme}>
            <ThemeProvider theme={theme}>
              <Provider>
                <Normalize />
                <FontStyle />
                <Global />
                <StyledSkipLink
                  id="skip-link"
                  color="secondary.1"
                  textStyle="body.smallPrint.regular"
                  padding={2}
                  as="a"
                  href="#mainContent"
                >
                  Skip to content
                </StyledSkipLink>
                <div id="application-form">
                  {evaluatePageToRender(featureSwitches)}
                </div>
              </Provider>
            </ThemeProvider>
          </MuiThemeProvider>
        )}
      </CacheProvider>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const initialProps = await NextApp.getInitialProps(appContext)
  const nonce = getValueFromHeader(appContext.ctx.req?.headers?.['x-nonce'])
  return {
    nonce,
    ...initialProps
  }
}

export default App
